import React from 'react'
import AdjustIcon from '@mui/icons-material/Adjust'
import Battery5BarIcon from '@mui/icons-material/Battery5Bar'
import BuildIcon from '@mui/icons-material/Build'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CarRepairIcon from '@mui/icons-material/CarRepair'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LockIcon from '@mui/icons-material/Lock'
import OilBarrelIcon from '@mui/icons-material/OilBarrel'
import OpacityIcon from '@mui/icons-material/Opacity'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import ShieldIcon from '@mui/icons-material/Shield'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import TireRepairIcon from '@mui/icons-material/TireRepair'
import ToysIcon from '@mui/icons-material/Toys'
import TuneIcon from '@mui/icons-material/Tune'
import WavesIcon from '@mui/icons-material/Waves'
import { REPAIR_CATEGORY_ICON_KEYS } from '@otw/models'

/**
 * Registry of `RepairCategoryDoc.iconKey` → React icon component. Keys are stable
 * strings persisted on Firestore docs (see `REPAIR_CATEGORY_ICON_KEYS` in @otw/models).
 * Includes a human-readable label for the icon picker UI.
 */
export interface RepairCategoryIconOption {
  key: string
  label: string
  Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' | 'inherit' }>
}

const REGISTRY: Record<string, RepairCategoryIconOption> = {
  // Built-in defaults — pick visually distinct silhouettes so the tab strip reads as a varied set.
  settings: { key: 'settings', label: 'Powertrain', Icon: PrecisionManufacturingIcon },
  brake_disc: { key: 'brake_disc', label: 'Brake disc', Icon: AdjustIcon },
  tire: { key: 'tire', label: 'Tire', Icon: TireRepairIcon },
  bolt: { key: 'bolt', label: 'Electrical', Icon: ElectricBoltIcon },
  car: { key: 'car', label: 'Body / Car', Icon: CarRepairIcon },
  other: { key: 'other', label: 'Other', Icon: HelpOutlineIcon },
  // Extra options users can pick when adding/editing custom categories.
  build: { key: 'build', label: 'Wrench', Icon: BuildIcon },
  engine: { key: 'engine', label: 'Tune', Icon: TuneIcon },
  oil: { key: 'oil', label: 'Oil', Icon: OilBarrelIcon },
  battery: { key: 'battery', label: 'Battery', Icon: Battery5BarIcon },
  lightbulb: { key: 'lightbulb', label: 'Lights', Icon: LightbulbIcon },
  fan: { key: 'fan', label: 'Fan / Cooling', Icon: ToysIcon },
  thermostat: { key: 'thermostat', label: 'Thermostat', Icon: ThermostatIcon },
  spring: { key: 'spring', label: 'Suspension', Icon: WavesIcon },
  shield: { key: 'shield', label: 'Shield', Icon: ShieldIcon },
  camera: { key: 'camera', label: 'Camera', Icon: CameraAltIcon },
  lock: { key: 'lock', label: 'Lock / Anti-theft', Icon: LockIcon },
  gas: { key: 'gas', label: 'Fuel', Icon: LocalGasStationIcon },
  water: { key: 'water', label: 'Fluid', Icon: OpacityIcon },
  truck: { key: 'truck', label: 'Truck', Icon: LocalShippingIcon },
}

export const REPAIR_CATEGORY_ICON_OPTIONS: RepairCategoryIconOption[] =
  REPAIR_CATEGORY_ICON_KEYS.map(key => REGISTRY[key]).filter(Boolean)

/** Default fallback icon when no iconKey is set or the key is unknown. */
export const getRepairCategoryIcon = (
  iconKey: string | null | undefined,
): RepairCategoryIconOption => {
  if (iconKey && REGISTRY[iconKey]) return REGISTRY[iconKey]
  return REGISTRY.build
}
